import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';

/**
 * Custom "I'm not a robot" checkbox — fully self-hosted, no third-party JS.
 * Pairs with a backend that also requires:
 *   - a hidden honeypot field that bots will fill
 *   - a min-elapsed-ms gate to filter out instant-submit bots
 * Calls onChange(true) once the user clicks + a short verify animation finishes.
 */
export default function HumanCheck({ verified, onChange }) {
  const [busy, setBusy] = useState(false);

  const handleClick = () => {
    if (verified || busy) return;
    setBusy(true);
    // ~700ms "thinking" delay so the click feels like a real verification step.
    setTimeout(() => {
      setBusy(false);
      onChange(true);
    }, 700);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy || verified}
      data-testid="human-check"
      aria-pressed={verified}
      className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 border transition-all ${
        verified
          ? 'bg-emerald-500/10 border-emerald-400/40'
          : 'bg-white/[0.04] border-white/15 hover:border-white/30'
      }`}
    >
      <span
        className={`w-6 h-6 rounded flex items-center justify-center border-2 transition-all ${
          verified
            ? 'bg-emerald-500 border-emerald-500'
            : busy
              ? 'border-blue-400 bg-blue-500/10'
              : 'border-slate-400 bg-white/[0.02]'
        }`}
        aria-hidden="true"
      >
        {busy && <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />}
        {verified && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
      </span>
      <span className={`text-sm ${verified ? 'text-emerald-300' : 'text-slate-200'}`}>
        {verified ? "You're verified, thanks!" : busy ? 'Verifying…' : 'Verify you are human'}
      </span>
    </button>
  );
}
